import { formatMoney } from "utils/money";

/**
 * How an order's timings read in a table cell.
 *
 * ONE PLACE, because the same three questions are asked on the orders page, on
 * the delivery-orders page and on the order's own screen, and a duration that
 * disagrees with itself across two screens is the failure this codebase has
 * already paid for once.
 *
 * The API owns the pickup ARITHMETIC (`pickup_delta_seconds`, computed from
 * `util/driverPerformance.js` so it can never disagree with the driver's own
 * score). Nothing here recomputes it — this file only decides how a number
 * already decided elsewhere is written down.
 */

/** Sign convention, fixed by the server: positive is early or on time. */
export const isPickupOnTime = (seconds) => Number(seconds) >= 0;

/**
 * The pickup delta as «مبكر ٤ د» / «متأخر ٦ د» / «في الوقت».
 *
 * Returns null when there is nothing to say, which the caller renders as its
 * own message — an order with no pickup stamp is NOT an on-time pickup, and
 * printing "0" for one would be a lie the operator cannot see through.
 */
export const formatPickupDelta = (seconds) => {
    if (seconds === null || seconds === undefined) return null;

    const value = Number(seconds);
    if (!Number.isFinite(value)) return null;

    const minutes = Math.round(Math.abs(value) / 60);

    if (minutes === 0) return "في الوقت";
    return value > 0 ? `مبكر ${minutes} د` : `متأخر ${minutes} د`;
};

/**
 * Whole minutes between two instants, or null when either is missing.
 *
 * Missing is the normal case rather than an error: a live order has no delivery
 * time yet, and an order from before these columns existed never will.
 */
export const minutesBetween = (from, to) => {
    if (!from || !to) return null;

    const start = new Date(from).getTime();
    const end = new Date(to).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

    return Math.round((end - start) / 60000);
};

/** A duration in minutes as «٢٥ د» or «١ س ٥ د» — hours only when there are any. */
export const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return null;

    const value = Number(minutes);
    if (!Number.isFinite(value)) return null;
    if (value < 60) return `${value} د`;

    const hours = Math.floor(value / 60);
    const rest = value % 60;
    return rest === 0 ? `${hours} س` : `${hours} س ${rest} د`;
};

// The table cells use the dashboard's one date format.
export { formatDateTime } from "./dateFormat";

/** Re-exported so a timing cell showing money does not import two modules. */
export { formatMoney };
